import { v4 as uuidv4 } from 'uuid'
import { stoppagesAjax } from '../ajax/StoppagesAjax'
import { convertDatesToUTC } from '../ajax/controller/AjaxController'
import { store } from '../state/store'
import type { ActionResponse } from '../DTOs/Common/ErrorResponse'
import type { StoppageDto } from '../DTOs/Stoppage/StoppageDto'
import { computeShiftId } from './computeShiftId'
import { enqueue } from './queueOps'
import { cachedPlantFor, patchCachedStoppages } from './patchCachedStoppages'
import { nextTempId } from './tempId'
import { isOnlineNow } from './connectivity'

function currentShiftId(): number {
  const { selectedDate, selectedShift } = store.getState().shift
  return computeShiftId(selectedDate, selectedShift)
}

function serialize(obj: unknown): unknown {
  return JSON.parse(JSON.stringify(convertDatesToUTC(obj)))
}

function withoutNavProps(dto: StoppageDto): StoppageDto {
  const { plant: _p, stoppageType: _st, stoppageSubType: _sst, location: _l, ...fields } = dto
  return fields as StoppageDto
}

export type QueuedResponse<T> = ActionResponse<T> & { queued: true };

export function wasQueued(result: ActionResponse<unknown> | { queued?: boolean }): boolean {
  return (result as { queued?: boolean }).queued === true;
}

export const stoppagesMutations = {
  async addStoppage(dto: StoppageDto) {
    if (isOnlineNow()) return stoppagesAjax.addStoppage(withoutNavProps(dto))
    const tempId = uuidv4()
    const tempStoppageId = await nextTempId()
    await enqueue({
      entity: 'stoppage',
      operation: 'create',
      shiftId: currentShiftId(),
      tempId,
      entityId: tempStoppageId,
      data: serialize(dto),
    })
    const queuedAdd: QueuedResponse<StoppageDto> = {
      success: true,
      queued: true,
      data: {
        ...dto,
        plant: dto.plant ?? cachedPlantFor(dto.plantId),
        stoppageId: tempStoppageId,
      } as StoppageDto,
    }
    return queuedAdd
  },

  async updateStoppage(dto: StoppageDto) {
    if (isOnlineNow()) return stoppagesAjax.updateStoppage(withoutNavProps(dto))
    await enqueue({
      entity: 'stoppage',
      operation: 'update',
      shiftId: currentShiftId(),
      entityId: dto.stoppageId,
      rowVersion: dto.rowVersion,
      data: serialize(withoutNavProps(dto)),
    })
    const merged = patchCachedStoppages(dto)
    const queuedUpdate: QueuedResponse<StoppageDto> = { success: true, queued: true, data: merged ?? dto }
    return queuedUpdate
  },

  async deleteStoppage(id: number, rowVersion?: string) {
    if (isOnlineNow()) return stoppagesAjax.deleteStoppage(id)
    await enqueue({
      entity: 'stoppage',
      operation: 'delete',
      shiftId: currentShiftId(),
      entityId: id,
      rowVersion,
    })
    const queuedDelete: QueuedResponse<null> = { success: true, queued: true, data: null }
    return queuedDelete
  },
}
